import type { PromptResolve } from '@ur-solutions/react-components'
import { Icon } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import {
  Button,
  Checkbox,
  Input,
  NumberInput,
  RegularModal,
  Select,
  UserSelect,
} from '@/components_deprecated'
import { DetectBlur } from '@/containers/util-containers'
import type { IssueCategoryForSettings } from '..'
import { Field, Form } from '..'
import { Color, SettingsColorPicker } from '../common'
import type { IssueSeverity, LocalSubcategory } from '@/modules/issues'
import { DELETE_SUBCATEGORY } from '@/modules/issues'
import { useIssueSeverityOptions } from '@/modules/issues/consts'
import styled from 'styled-components'
import React, { useState } from 'react'
import { useMutation } from '@apollo/client'

const Modal = styled(RegularModal)`
  .content {
    display: flex;

    & > div:first-child {
      width: 50%;
      padding-right: 1rem;
    }

    & > div:last-child {
      width: 50%;
      padding-left: 1rem;
      border-left: 1px solid ${props => props.theme.colors.gray9};
    }
  }
`

const ExplanationLabel = styled.label`
  margin: 6px 0 6px 6px !important;
`

const StyledButton = styled(Button)`
  padding: 8px;
  width: 3rem;
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 3rem;
`
const ContainerSubCategory = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`

const SubCategoryItem = styled.div<{ background: string }>`
  padding: 4px 12px;
  border-radius: 40px;
  width: fit-content;
  background: ${props => props.background};
  display: flex;
  gap: 1rem;
  flex-direction: row;
  cursor: pointer;
`
const ContainerLabel = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
`
const CheckBoxField = styled(Field)`
  & > div {
    justify-content: left;
  }
`

export interface IssueCategoryForm {
  name: string
  color: string
  responsibleUser?: string | null
  caseHandler?: string | null
  automaticReminderInterval: number
  minimumSeverityForReminder: IssueSeverity | null
  remindResponsible: boolean
  remindCreator: boolean
  subcategories: LocalSubcategory[]
  newSubCategory: LocalSubcategory
}

interface IssueCategoryPromptProps {
  category?: IssueCategoryForSettings | null
  initialData?: IssueCategoryForm
  categories: IssueCategoryForSettings[]
  onSubmit: PromptResolve<IssueCategoryForm | null>
}

export const IssueCategoryPrompt: React.FC<IssueCategoryPromptProps> = ({
  category,
  initialData,
  categories,
  onSubmit,
}) => {
  const translations = useTranslate({
    form: {
      label: {
        name: 'common.name',
        color: 'common.color',
        responsibleUser: 'settings.responsible-user',
        caseHandler: 'settings.case-handler',
        automaticReminderInterval: 'settings.automatic-reminder-interval',
        automaticReminderIntervalInfo:
          'settings.automatic-reminder-interval-explaination',
        minimumSeverityForReminder: 'settings.minimum-severity-for-reminder',
        remindResponsibleUser: 'settings.remind-responsible-user',
        remindCreator: 'settings.remind-creator',
      },
    },

    addIssueCategory: 'settings.add-issue-category',
    editIssueCategory: 'settings.edit-issue-category',
    issueCategories: 'common.issue-categories',

    create: 'common.create-alt',
    update: 'common.update',

    validation: {
      nameExists: 'settings.validation.issue-category-name-exists',
      required: 'common.required',
    },
    prompt: {
      delete: ['settings.prompts.issue-category-delete', { name: '' }],
      deleteTitle: 'settings.prompts.issue-category-delete-title',
    },
    defaultSeverity: 'common.default-severity',
    subCategory: 'common.sub-category',
    deleteError: 'common.error-delete',
  })
  const severityOptions = useIssueSeverityOptions()
  const editing = typeof initialData !== 'undefined'
  const [deleteSubcategoryMutation] = useMutation(DELETE_SUBCATEGORY)
  const [showSubModal, setShowSubModal] = useState(false)

  function validateName(name: string, formName: string) {
    if (name === initialData?.name) return null
    if (name === '') return translations.validation.required
    if (categories.find(type => type.name === formName)) {
      return translations.validation.nameExists
    }
    return null
  }

  const {
    formValues: form,
    formErrors: errors,
    formValid,
    formChangeHandler: handler,
    updateForm,
    submitHandler: submit,
  } = useForm<IssueCategoryForm>({
    values: initialData ?? {
      name: '',
      color: 'hsl(138deg, 80%, 80%)',
      responsibleUser: null,
      caseHandler: null,
      automaticReminderInterval: 0,
      minimumSeverityForReminder: 'HIGH',
      remindResponsible: false,
      remindCreator: false,
      newSubCategory: {
        name: '',
        defaultSeverity: 'MEDIUM',
      },
      subcategories: (category?.issueSubcategories?.edges ?? []).map(el => ({
        id: el.node.id,
        name: el.node.name,
        defaultSeverity: el.node?.defaultSeverity,
      })),
    },
    validators: {
      name: (val, form) => validateName(val, form.name),
    },
  })

  const hasRequiredFields = Boolean(form.name.trim() && form.responsibleUser)

  const openSubModalForNew = () => {
    updateForm({ newSubCategory: { name: '', defaultSeverity: 'MEDIUM' } })
    setShowSubModal(true)
  }
  const openSubModalForEdit = (index: number) => {
    const sub = form.subcategories[index]
    if (sub) {
      updateForm({ newSubCategory: { ...sub } })
      setShowSubModal(true)
    }
  }

  const handleSubModalSubmit = (sub: LocalSubcategory) => {
    let newSubcategories = [...form.subcategories]

    if (sub.id) {
      newSubcategories = newSubcategories.map(s => (s.id === sub.id ? sub : s))
    } else {
      newSubcategories.push(sub)
    }

    updateForm({
      subcategories: newSubcategories,
      newSubCategory: { name: '', defaultSeverity: 'MEDIUM' },
    })
    setShowSubModal(false)
  }

  const handleDeleteSubcategory = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    try {
      await deleteSubcategoryMutation({
        variables: { id },
      })

      updateForm({
        subcategories: form?.subcategories?.filter(s => s.id !== id),
      })
    } catch (err) {
      alert(translations.deleteError)
      console.error(err)
    }
  }

  if (showSubModal) {
    return (
      <DetectBlur active onClickOutside={() => setShowSubModal(false)}>
        <RegularModal
          title={translations.subCategory}
          submitText={translations.create}
          submitDisabled={!form.newSubCategory.name.trim()}
          width="300px"
          onCancel={() => setShowSubModal(false)}
          onSubmit={() => handleSubModalSubmit(form.newSubCategory)}
        >
          <Form>
            <Field>
              <label>{translations.defaultSeverity}</label>
              <Select
                value={form.newSubCategory?.defaultSeverity}
                options={severityOptions}
                nullable={false}
                onChange={value =>
                  value &&
                  updateForm({
                    newSubCategory: {
                      ...form.newSubCategory,
                      defaultSeverity: value,
                    },
                  })
                }
                fullWidth
                grayed
              />
            </Field>
            <Field>
              <label>{translations.form.label.name}</label>
              <Input
                value={form.newSubCategory.name}
                autoFocus
                fullWidth
                grayed
                onChange={e =>
                  updateForm({
                    newSubCategory: {
                      ...form.newSubCategory,
                      name: e,
                    },
                  })
                }
              />
            </Field>
          </Form>
        </RegularModal>
      </DetectBlur>
    )
  }

  return (
    <DetectBlur active onClickOutside={() => onSubmit(null)}>
      <Modal
        title={
          editing
            ? translations.editIssueCategory
            : translations.addIssueCategory
        }
        submitText={editing ? translations.update : translations.create}
        submitDisabled={!formValid || !hasRequiredFields}
        width="800px"
        onCancel={() => onSubmit(null)}
        onSubmit={submit(onSubmit)}
      >
        <div>
          <Form>
            <Field>
              <label>{translations.form.label.name}</label>
              <Input
                value={form.name}
                error={errors.name}
                autoFocus
                fullWidth
                onChange={handler('name')}
                grayed
              />
            </Field>

            <Field>
              <label>{translations.form.label.responsibleUser}</label>
              <UserSelect
                value={form.responsibleUser ?? null}
                fullWidth
                onChange={responsibleUser =>
                  updateForm({ responsibleUser: responsibleUser })
                }
                grayed
              />
            </Field>
            <Field>
              <label>{translations.form.label.caseHandler}</label>
              <UserSelect
                value={form.caseHandler ?? null}
                fullWidth
                onChange={caseHandler => updateForm({ caseHandler: caseHandler })}
                grayed
              />
            </Field>
            <Field>
              <label>{translations.form.label.color}</label>
              <Color color={form.color}>
                <SettingsColorPicker
                  value={form.color}
                  onChange={handler('color')}
                />
              </Color>
            </Field>
          </Form>
          {form.responsibleUser && form.name && (
            <div>
              <ContainerLabel>
                <label>{translations.subCategory}</label>
                <StyledButton type="button" onClick={openSubModalForNew}>
                  <Icon icon="plus" />
                </StyledButton>
              </ContainerLabel>

              <ContainerSubCategory>
                {form.subcategories?.map((sub, index) => (
                  <SubCategoryItem
                    key={sub.id}
                    background={form.color}
                    onClick={() => openSubModalForEdit(index)}
                  >
                    <span>
                      {sub.name} ({sub.defaultSeverity})
                    </span>
                    <span
                      onClick={e => handleDeleteSubcategory(e, sub?.id ?? '')}
                    >
                      <Icon icon="trash" color="black" />
                    </span>
                  </SubCategoryItem>
                ))}
              </ContainerSubCategory>
            </div>
          )}
        </div>
        <div>
          <Form>
            <Field>
              <label>{translations.form.label.automaticReminderInterval}</label>
              <NumberInput
                value={form.automaticReminderInterval}
                onChange={handler('automaticReminderInterval')}
                min={0}
                grayed
              />
              <ExplanationLabel>
                {translations.form.label.automaticReminderIntervalInfo}
              </ExplanationLabel>
            </Field>
            {form.automaticReminderInterval !== null &&
              form.automaticReminderInterval !== 0 &&
              form.automaticReminderInterval !== undefined && (
                <>
                  <Field>
                    <label>
                      {translations.form.label.minimumSeverityForReminder}
                      <Select
                        value={form.minimumSeverityForReminder}
                        options={severityOptions}
                        nullable={false}
                        onChange={value =>
                          value
                            ? updateForm({ minimumSeverityForReminder: value })
                            : void 0
                        }
                        fullWidth
                        grayed
                      />
                    </label>
                  </Field>
                  <CheckBoxField>
                    <Checkbox
                      checked={form.remindResponsible}
                      onChange={handler('remindResponsible')}
                      label={translations.form.label.remindResponsibleUser}
                      grayed
                    />
                  </CheckBoxField>
                  <CheckBoxField>
                    <Checkbox
                      checked={form.remindCreator}
                      onChange={handler('remindCreator')}
                      label={translations.form.label.remindCreator}
                      grayed
                    />
                  </CheckBoxField>
                </>
              )}
          </Form>
        </div>
      </Modal>
    </DetectBlur>
  )
}
